import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { FaCheckCircle, FaExclamationTriangle } from "react-icons/fa";
import {
  assignScreenerToClass,
  getAssessmentScreener,
  getClasses,
  getCountryId
} from "../services/APIClient";
import {
  assertConfirmedMutationResponse
} from "../utils/teacherWorkflowRequests";
import "./TeacherAssignScreenerModal.css";

const getCurrentAcademicYearLabel = (date = new Date()) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const startYear = month >= 8 ? year : year - 1;

  return `${startYear}/${String(startYear + 1).slice(-2)}`;
};

const BASE_SCREENER_OPTIONS = [
  {
    id: "baseline",
    windowId: "baseline",
    name: "Baseline Screener",
    description: "Available for class assignment now.",
    aliases: ["baseline"]
  },
  {
    id: "midpoint",
    windowId: "midpoint",
    name: "Midpoint Screener",
    description: "Use when the midpoint screening window opens.",
    aliases: ["midpoint", "mid point", "mid-year", "mid year", "midyear"]
  },
  {
    id: "end-of-year",
    windowId: "end-of-year",
    name: "End Of Year Screener",
    description: "Use when the end-of-year screening window opens.",
    aliases: ["end-of-year", "end of year", "end year", "eoy"]
  }
];

const GRADE_LEVELS = [
  { value: "grade_level1", label: "Junior Infants" },
  { value: "grade_level2", label: "Senior Infants" },
  { value: "grade_level3", label: "1st Class" },
  { value: "grade_level4", label: "2nd Class" },
  { value: "grade_level5", label: "3rd Class" },
  { value: "grade_level6", label: "4th Class" },
  { value: "grade_level7", label: "5th Class" },
  { value: "grade_level8", label: "6th Class" }
];

const normaliseText = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ");

const normaliseComparableText = (value) => normaliseText(value).toLowerCase();

const getWindowIdFromScreenerText = (value) => {
  const comparableValue = normaliseComparableText(value).replace(/[-_]+/g, " ");

  if (!comparableValue) {
    return "";
  }

  const matchingScreener = BASE_SCREENER_OPTIONS.find((screener) =>
    [screener.id, screener.windowId, screener.name, ...(screener.aliases || [])].some(
      (alias) => normaliseComparableText(alias).replace(/[-_]+/g, " ") === comparableValue ||
        comparableValue.includes(normaliseComparableText(alias).replace(/[-_]+/g, " "))
    )
  );

  return matchingScreener?.windowId || "";
};

const getClassId = (classItem) => classItem?.id || classItem?._id;

const getGradeLabelFromClass = (classItem) => {
  const gradeLevel = GRADE_LEVELS.find((grade) => classItem?.[grade.value]);
  return gradeLevel?.label || classItem?.grade || classItem?.gradeLevel || classItem?.grade_level || "";
};

const getGradeLevelNumberFromClass = (classItem) => {
  const gradeLevel = GRADE_LEVELS.find((grade) => classItem?.[grade.value]);
  if (gradeLevel) {
    return Number(gradeLevel.value.replace("grade_level", ""));
  }

  const numericGrade = Number(
    classItem?.gradeLevelNumber ||
    classItem?.grade_level_number ||
    classItem?.gradeLevel ||
    classItem?.grade_level
  );

  return Number.isFinite(numericGrade) ? numericGrade : null;
};

const getAcademicYearFromClass = (classItem) =>
  classItem?.academicYear ||
  classItem?.academic_year ||
  classItem?.academicyear ||
  classItem?.academicYearLabel ||
  classItem?.yearLabel ||
  classItem?.schoolYear ||
  "";

const getStudentCountFromClass = (classItem) => {
  const possibleCount =
    classItem?.studentNumber ??
    classItem?.studentCount ??
    classItem?.studentsCount ??
    classItem?.pupilCount ??
    classItem?.students?.length;

  const numericCount = Number(possibleCount);
  return Number.isFinite(numericCount) ? numericCount : null;
};

const getScreenerId = (screener) =>
  screener?.assessmentId ||
  screener?.assessmentid ||
  screener?.assessment_id ||
  screener?.id ||
  screener?._id;

const getScreenerName = (screener) =>
  screener?.assessmentName ||
  screener?.assessment_name ||
  screener?.name ||
  screener?.title ||
  screener?.label ||
  getScreenerId(screener) ||
  "Screener";

const getMatchingBaseScreener = (screener) => {
  const id = getScreenerId(screener);
  const name = getScreenerName(screener);
  const windowId = getWindowIdFromScreenerText(name) || getWindowIdFromScreenerText(id);

  return BASE_SCREENER_OPTIONS.find((baseScreener) => baseScreener.windowId === windowId) || null;
};

const normaliseScreener = (screener) => {
  const matchingBaseScreener = getMatchingBaseScreener(screener);
  const id = getScreenerId(screener);
  const name = getScreenerName(screener);

  return {
    ...(matchingBaseScreener || {}),
    ...screener,
    id,
    windowId: matchingBaseScreener?.windowId || getWindowIdFromScreenerText(name) || id,
    name: matchingBaseScreener?.name || name,
    description: matchingBaseScreener?.description || screener?.description || "Available for class assignment",
    aliases: matchingBaseScreener?.aliases || []
  };
};

const getScreenerSortOrder = (screener) => {
  const index = BASE_SCREENER_OPTIONS.findIndex((baseScreener) => baseScreener.windowId === screener.windowId);
  return index === -1 ? BASE_SCREENER_OPTIONS.length : index;
};

const getCountryIdFromResponse = (response) =>
  response?.countryId ||
  response?.countryid ||
  response?.data?.countryId ||
  response?.data?.countryid ||
  "";

const buildScreenerOptions = (assessmentScreenersResponse) => {
  const assessmentScreeners = Array.isArray(assessmentScreenersResponse) ? assessmentScreenersResponse : [];
  return assessmentScreeners
    .filter((screener) => screener?.active !== false)
    .filter((screener) => screener?.task !== true)
    .map(normaliseScreener)
    .filter((screener) => screener.id)
    .sort((firstScreener, secondScreener) => {
      const orderDifference = getScreenerSortOrder(firstScreener) - getScreenerSortOrder(secondScreener);
      return orderDifference || firstScreener.name.localeCompare(secondScreener.name);
    });
};

const normaliseClass = (classItem) => {
  const grade = getGradeLabelFromClass(classItem);
  const academicYear = getAcademicYearFromClass(classItem);
  const studentCount = getStudentCountFromClass(classItem);
  const studentCopy =
    studentCount === null
      ? ""
      : `${studentCount} ${studentCount === 1 ? "child" : "children"}`;
  const detailLabel = [grade, academicYear, studentCopy].filter(Boolean).join(", ");

  return {
    ...classItem,
    id: getClassId(classItem),
    name: classItem?.name || classItem?.className || "Unnamed class",
    grade,
    academicYear,
    studentCount,
    detailLabel
  };
};

const normalisePrefill = (prefill) => ({
  className: normaliseText(prefill?.className || prefill?.class_name),
  screenerName: normaliseText(prefill?.screenerName || prefill?.screener_name)
});

const findSingleExactClass = (classes, className) => {
  const target = normaliseComparableText(className);

  if (!target) {
    return null;
  }

  const matches = classes.filter(
    (classItem) => normaliseComparableText(classItem.name) === target
  );

  return matches.length === 1 ? matches[0] : null;
};

const findSingleExactScreener = (screeners, screenerName) => {
  const target = normaliseComparableText(screenerName);
  const targetWindowId = getWindowIdFromScreenerText(screenerName);

  if (!target) {
    return null;
  }

  const matches = screeners.filter(
    (screener) =>
      normaliseComparableText(screener.name) === target ||
      (targetWindowId && screener.windowId === targetWindowId)
  );

  return matches.length === 1 ? matches[0] : null;
};

function TeacherAssignScreenerModal({
  show,
  handleClose,
  schoolid,
  prefill = null,
  onViewQrCodes = null,
  embedded = false,
  onWorkflowComplete = null
}) {
  const [stage, setStage] = useState("choose");
  const [classes, setClasses] = useState([]);
  const [classSearchTerm, setClassSearchTerm] = useState("");
  const [isClassPickerExpanded, setIsClassPickerExpanded] = useState(true);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedScreener, setSelectedScreener] = useState(null);
  const [screenerOptions, setScreenerOptions] = useState([]);
  const [completedAssignment, setCompletedAssignment] = useState(null);
  const [isLoadingClasses, setIsLoadingClasses] = useState(false);
  const [isLoadingScreeners, setIsLoadingScreeners] = useState(false);
  const [schoolCountryId, setSchoolCountryId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const prefillContext = useMemo(() => normalisePrefill(prefill), [prefill]);
  const trimmedClassSearchTerm = classSearchTerm.trim();
  const currentAcademicYear = getCurrentAcademicYearLabel();
  const canAssign = Boolean(selectedClass && selectedScreener && !isLoadingScreeners && !isSubmitting);

  useEffect(() => {
    if (!show) {
      return;
    }

    setStage("choose");
    setClasses([]);
    setClassSearchTerm(prefillContext.className);
    setIsClassPickerExpanded(true);
    setSelectedClass(null);
    setSelectedScreener(null);
    setScreenerOptions([]);
    setSchoolCountryId("");
    setCompletedAssignment(null);
    setIsSubmitting(false);
    setError("");
    setIsLoadingClasses(true);

    Promise.all([
      getClasses(),
      schoolid ? getCountryId(schoolid) : Promise.resolve(null)
    ])
      .then(([classesResponse, countryResponse]) => {
        const normalisedClasses = (Array.isArray(classesResponse) ? classesResponse : [])
          .map(normaliseClass)
          .filter((classItem) => classItem.id)
          .sort((classA, classB) => classA.name.localeCompare(classB.name));
        const exactClass = findSingleExactClass(normalisedClasses, prefillContext.className);

        setClasses(normalisedClasses);
        setSchoolCountryId(getCountryIdFromResponse(countryResponse));
        setSelectedClass(exactClass);
        setIsClassPickerExpanded(!exactClass);
      })
      .catch((classesError) => {
        console.error("Error loading classes for screener assignment:", classesError);
        setClasses([]);
        setError("Classes could not be loaded. Please try again.");
      })
      .finally(() => {
        setIsLoadingClasses(false);
      });
  }, [show, prefillContext.className, schoolid]);

  useEffect(() => {
    if (!show || !selectedClass) {
      setScreenerOptions([]);
      setSelectedScreener(null);
      return;
    }

    const gradeLevel = getGradeLevelNumberFromClass(selectedClass);

    if (!schoolCountryId || !gradeLevel) {
      setScreenerOptions([]);
      setSelectedScreener(null);
      setError("Screeners could not be loaded for this class. Please check the school country and class grade.");
      return;
    }

    setIsLoadingScreeners(true);
    setScreenerOptions([]);
    setSelectedScreener(null);
    setError("");

    getAssessmentScreener({
      countryid: schoolCountryId,
      grade_level: gradeLevel,
      active: true,
      task: false
    })
      .then((screenersResponse) => {
        const nextScreenerOptions = buildScreenerOptions(screenersResponse);

        if (nextScreenerOptions.length < 1) {
          setError("No active screeners are available for this class grade and country.");
        }

        setScreenerOptions(nextScreenerOptions);
        setSelectedScreener(findSingleExactScreener(nextScreenerOptions, prefillContext.screenerName));
      })
      .catch((screenersError) => {
        console.error("Error loading screeners for class assignment:", screenersError);
        setScreenerOptions([]);
        setSelectedScreener(null);
        setError("Screeners could not be loaded for this class. Please try again.");
      })
      .finally(() => {
        setIsLoadingScreeners(false);
      });
  }, [show, selectedClass, schoolCountryId, prefillContext.screenerName]);

  const currentYearClasses = useMemo(() => {
    const matchingClasses = classes.filter(
      (classItem) => classItem.academicYear === currentAcademicYear
    );

    return matchingClasses.length > 0 ? matchingClasses : classes;
  }, [classes, currentAcademicYear]);

  const filteredClasses = useMemo(() => {
    const query = normaliseComparableText(trimmedClassSearchTerm);

    if (!query) {
      return currentYearClasses;
    }

    return currentYearClasses.filter((classItem) =>
      [classItem.name, classItem.grade, classItem.academicYear, classItem.detailLabel].some(
        (value) => normaliseComparableText(value).includes(query)
      )
    );
  }, [currentYearClasses, trimmedClassSearchTerm]);

  const handleClassSearchChange = (event) => {
    const nextSearchTerm = event.target.value;
    setClassSearchTerm(nextSearchTerm);
    setIsClassPickerExpanded(true);
    setError("");

    if (
      selectedClass &&
      normaliseComparableText(nextSearchTerm) !== normaliseComparableText(selectedClass.name)
    ) {
      setSelectedClass(null);
    }
  };

  const handleAssign = async () => {
    if (!canAssign) {
      return;
    }

    if (selectedScreener.opensLater) {
      setStage("opensLater");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      if (!schoolid) {
        throw new Error("School context is missing.");
      }

      const response = await assignScreenerToClass(schoolid, selectedClass.id, selectedScreener.id);
      assertConfirmedMutationResponse(response, ["completed"]);

      setCompletedAssignment({
        classId: selectedClass.id,
        className: selectedClass.name,
        screenerName: selectedScreener.name,
        assignedCount: response.assignedCount,
        skippedCount: response.skippedCount
      });
      setStage("success");
    } catch (assignError) {
      console.error("Error assigning screener:", assignError);
      setError("The screener could not be assigned. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChooseBaseline = () => {
    const baselineScreener = screenerOptions.find((screener) => screener.windowId === "baseline") || screenerOptions[0];
    if (!baselineScreener) {
      setError("No available screeners could be loaded. Please try again.");
      return;
    }
    setSelectedScreener(baselineScreener);
    setStage("choose");
    setError("");
  };

  const resetForAnotherAssignment = () => {
    setStage("choose");
    setClassSearchTerm("");
    setIsClassPickerExpanded(true);
    setSelectedClass(null);
    setSelectedScreener(null);
    setCompletedAssignment(null);
    setError("");
  };

  const handleDone = () => {
    if (onWorkflowComplete) {
      onWorkflowComplete({
        reference: completedAssignment?.classId || completedAssignment?.className || "",
        message: "Screener assignment complete."
      });
      return;
    }

    handleClose();
  };

  const handleModalHide = () => {
    if (stage === "success") {
      handleDone();
      return;
    }

    handleClose();
  };

  const renderClassResults = () => {
    if (isLoadingClasses) {
      return (
        <div className="assign-screener-search-state">
          <Spinner animation="border" size="sm" />
          <span>Loading classes...</span>
        </div>
      );
    }

    if (filteredClasses.length < 1) {
      return (
        <p className="assign-screener-muted">
          No matching classes found in your class list.
        </p>
      );
    }

    return filteredClasses.map((classItem) => {
      const isSelected = selectedClass?.id === classItem.id;

      return (
        <button
          key={classItem.id}
          type="button"
          className={`assign-screener-result${isSelected ? " selected" : ""}`}
          onClick={() => {
            setSelectedClass(classItem);
            setClassSearchTerm(classItem.name);
            setIsClassPickerExpanded(false);
            setError("");
          }}
          aria-label={`Select ${classItem.name}${classItem.detailLabel ? ` - ${classItem.detailLabel}` : ""}`}
          aria-pressed={isSelected}
        >
          <span className="assign-screener-radio" aria-hidden="true" />
          <span className="assign-screener-result-copy">
            <strong>{classItem.name}</strong>
            {classItem.detailLabel && <span>{classItem.detailLabel}</span>}
          </span>
          {isSelected && <span className="assign-screener-selected-label">Selected</span>}
        </button>
      );
    });
  };

  const renderScreenerResults = () => {
    if (!selectedClass) {
      return (
        <p className="assign-screener-muted">
          Choose a class first to load screeners for its grade.
        </p>
      );
    }

    if (isLoadingScreeners) {
      return (
        <div className="assign-screener-search-state">
          <Spinner animation="border" size="sm" />
          <span>Loading screeners...</span>
        </div>
      );
    }

    if (screenerOptions.length < 1) {
      return (
        <p className="assign-screener-muted">
          No screeners are available for this class.
        </p>
      );
    }

    return screenerOptions.map((screener) => {
      const isSelected = selectedScreener?.id === screener.id;

      return (
        <button
          key={screener.id}
          type="button"
          className={`assign-screener-option${isSelected ? " selected" : ""}${screener.opensLater ? " opens-later" : ""}`}
          onClick={() => {
            setSelectedScreener(screener);
            setError("");
          }}
          aria-label={`Select ${screener.name}`}
          aria-pressed={isSelected}
        >
          <span className="assign-screener-radio" aria-hidden="true" />
          <span className="assign-screener-result-copy">
            <strong>{screener.name}</strong>
          </span>
          {isSelected && <span className="assign-screener-selected-label">Selected</span>}
        </button>
      );
    });
  };

  const renderSelectedSummary = () => {
    if (!selectedClass && !selectedScreener) {
      return null;
    }

    return (
      <div className="assign-screener-summary">
        <strong>Selected assignment</strong>
        <span className="assign-screener-summary-row">
          <span>Class</span>
          <span>{selectedClass?.name || "Choose a class"}</span>
        </span>
        <span className="assign-screener-summary-row">
          <span>Screener</span>
          <span>{selectedScreener?.name || "Choose a screener"}</span>
        </span>
      </div>
    );
  };

  const renderChoose = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Assign Screener</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="assign-screener-intro">
          Choose the class and screener to assign.
        </p>
        {error && <Alert variant="danger">{error}</Alert>}

        <Form.Group className="mb-3" controlId="assignScreenerClassSearch">
          <Form.Label>Which class needs a screener?</Form.Label>
          <Form.Text as="div" className="assign-screener-field-label">
            Search class
          </Form.Text>
          <Form.Control
            type="search"
            value={classSearchTerm}
            onChange={handleClassSearchChange}
            placeholder="Start typing a class name"
            aria-label="Search class"
          />
        </Form.Group>

        <div className="assign-screener-results" aria-label="Matching classes">
          {selectedClass && !isClassPickerExpanded ? (
            <button
              type="button"
              className="assign-screener-selected-class"
              onClick={() => {
                setSelectedClass(null);
                setClassSearchTerm("");
                setIsClassPickerExpanded(true);
              }}
              aria-label={`Change selected class ${selectedClass.name}`}
            >
              <span className="assign-screener-result-copy">
                <strong>{selectedClass.name}</strong>
                {selectedClass.detailLabel && <span>{selectedClass.detailLabel}</span>}
              </span>
              <span className="assign-screener-selected-label">Change</span>
            </button>
          ) : (
            renderClassResults()
          )}
        </div>

        <Form.Group className="mt-4 mb-3" controlId="assignScreenerSearch">
          <Form.Label>Which screener should be assigned?</Form.Label>
          <Form.Text as="div" className="assign-screener-field-label">
            Choose one of the available screener options.
          </Form.Text>
        </Form.Group>

        <div className="assign-screener-results" aria-label="Matching screeners">
          {renderScreenerResults()}
        </div>

        {renderSelectedSummary()}
      </Modal.Body>
      <Modal.Footer>
        <span className="assign-screener-footer-note">
          {canAssign ? "Ready to assign." : "Choose a class and screener first."}
        </span>
        <Button variant="outline-secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="primary" disabled={!canAssign} onClick={handleAssign}>
          Assign screener
        </Button>
      </Modal.Footer>
    </>
  );

  const renderOpensLater = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Screener Opens Later</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="warning" className="assign-screener-warning">
          <FaExclamationTriangle aria-hidden="true" />
          <span>
            <strong>{selectedScreener?.name} opens {selectedScreener?.openDateLabel}.</strong>
            <span>Choose an available screener to assign now.</span>
          </span>
        </Alert>
        {renderSelectedSummary()}
      </Modal.Body>
      <Modal.Footer>
        <span className="assign-screener-footer-note">This screener is not open yet.</span>
        <Button variant="outline-secondary" onClick={() => setStage("choose")}>
          Back
        </Button>
        <Button variant="primary" onClick={handleChooseBaseline}>
          Choose Baseline Screener
        </Button>
      </Modal.Footer>
    </>
  );

  const renderSuccess = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Screener Assigned</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="success" className="assign-screener-success">
          <FaCheckCircle aria-hidden="true" />
          <span>
            <strong>
              {completedAssignment?.screenerName} has been assigned to {completedAssignment?.className}.
            </strong>
            <span>Children in this class can scan their QR codes to complete the screener.</span>
            <span>
              {completedAssignment?.assignedCount ?? 0} assigned, {completedAssignment?.skippedCount ?? 0} skipped.
            </span>
          </span>
        </Alert>
        <div className="assign-screener-next">
          <strong>What happens next</strong>
          <span>{embedded ? "Assign another screener repeats the action." : "View QR codes opens the QR-code workflow for the class."}</span>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <span className="assign-screener-footer-note">
          {embedded ? "Done returns to SENSAI. Assign another screener repeats the action." : "Screener assigned. QR codes are the next step."}
        </span>
        <Button variant="outline-secondary" onClick={resetForAnotherAssignment}>
          Assign another screener
        </Button>
        <Button variant={embedded ? "primary" : "outline-secondary"} onClick={handleDone}>
          Done
        </Button>
        {!embedded && onViewQrCodes && (
          <Button
            variant="primary"
            onClick={() => onViewQrCodes()}
          >
            View QR codes
          </Button>
        )}
      </Modal.Footer>
    </>
  );

  return (
    <Modal
      show={show}
      onHide={handleModalHide}
      centered
      backdrop="static"
      className="teacher-assign-screener-modal"
    >
      {stage === "opensLater" && renderOpensLater()}
      {stage === "success" && renderSuccess()}
      {stage === "choose" && renderChoose()}
    </Modal>
  );
}

export default TeacherAssignScreenerModal;
